import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, Inbox, ChevronRight } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useFunder } from "@/funder/FunderContext";
import { useIntakes } from "@/intakes/IntakesContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spinner } from "@/components/ui-kit/Spinner";
import { DashboardMetrics } from "@/components/dashboard/DashboardMetrics";
import { NotificationList } from "@/components/dashboard/NotificationList";
import { AgingBreakdown } from "@/components/dashboard/AgingBreakdown";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function DashboardPage() {
  const { user } = useAuth();
  const { abbr, firmName } = useFunder();
  const { newCount } = useIntakes();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api
      .get("/dashboard")
      .then((r) => active && setData(r.data))
      .catch((e) => active && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => {
      active = false;
    };
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";
  const count = data?.notifications?.length || 0;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="text-sm text-muted-foreground">{firmName}</p>
        <h1 className="mt-1 font-serif text-3xl font-semibold tracking-tight text-foreground">
          {greeting()}, {firstName}.
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          {count > 0
            ? `${count} item${count === 1 ? "" : "s"} need your attention across your matters and invoices.`
            : "Everything is handled — no matters need billing and no invoices are overdue."}
        </p>
      </div>

      {user?.is_owner && newCount > 0 && (
        <button
          type="button"
          onClick={() => navigate("/settings")}
          data-testid="dashboard-intakes-tile"
          className="group flex w-full items-center gap-4 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 text-left transition hover:bg-primary/10"
        >
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary">
            <Inbox className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground">
              {newCount} new client {newCount === 1 ? "intake" : "intakes"}
            </p>
            <p className="text-sm text-muted-foreground">
              A referred client submitted the welcome page. Review and start a new client.
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
        </button>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive" data-testid="dashboard-error">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {!data && !error ? (
        <div className="grid place-items-center py-16">
          <Spinner />
        </div>
      ) : data ? (
        <>
          <DashboardMetrics metrics={data.metrics} funder={data.funder} />

          <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
            <section data-testid="notification-center">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-foreground">
                  <Bell className="h-4 w-4 text-muted-foreground" /> Needs your attention
                </h2>
                <span className="text-sm text-muted-foreground" data-testid="notification-count">{count} active</span>
              </div>
              <NotificationList items={data.notifications} />
            </section>

            <aside>
              <AgingBreakdown aging={data.aging} />
            </aside>
          </div>
        </>
      ) : null}
    </div>
  );
}
